import { useEffect, useState } from "react";
import useAppContext from "../components/AppContext";
import { useFetchBundle, useFetchPlans } from "./plans";
import normalizeClinicalPlan from "../components/ClinicCheckout/Plans/components/OfferCards/normalize-plan";
import normalizePlan from "../utils/normalizePlan";
import { filter } from "lodash";

const WW_OWNER_ID = "37";
/**
 *
 * @param {object} props
 * @param {string} props.bundleId
 * @returns
 */
export const useBundle = ({ bundleId, isCompound }) => {
  const { contact, newQuiz } = useAppContext();
  const [bundle, setBundle] = useState({
    contentMap: new Map(),
    addonTemplateContentMap: new Map(),
    templateContentMap: new Map(),
    contents: [],
    plans: []
  });
  const [plansId, setPlansId] = useState([]);
  const [plans, setPlans] = useState([]);
  const { market: locale } = contact;
  const { data, isLoading, mutate, isError, error } = useFetchBundle(bundleId);
  let {
    data: plansData,
    isLoading: arePlansLoading,
    isError: plansIsError
  } = useFetchPlans(plansId, locale, newQuiz.isSequence);

  const subscriptionType = basePlan =>
    filter(basePlan.customFields, ["name", "subscriptionType"])[0]?.value;

  useEffect(() => {
    if (data && !isLoading) {
      const contentMap = getContentMap({ data: data?.contents });
      const plans = data.plans?.[WW_OWNER_ID];
      const plansId = plans?.offerPlans?.map(plan => plan.offerPlanId);

      setPlansId(plansId);
      setBundle({
        ...data,
        contentMap,
        bestOfferPlanId: plans?.bestOfferPlanId,
        preSelectedOfferPlanId: plans?.preSelectedOfferPlanId
      });
    }
  }, [data]);

  useEffect(() => {
    if (plansData && !arePlansLoading && !plans.length) {
      const plans = [];

      plansData.forEach(plan => {
        // Return if the same offer plan is already in the array.
        // This can happen if the user is B2B and the same offer plan is returned for all plans.
        const hasPlan = plans.find(p => p.id === plan.id);
        if (hasPlan) return;

        const contentMap = getContentMap({
          data: [...plan.contents, ...plan.plan.contents],
          filterFunc: content =>
            content.locale === locale && content.state === "active"
        });

        const addonTemplateContentMap = getAddonTemplateContentMap({
          data: plan.addons,
          filterFunc: addon => addon?.templateContent?.locale === locale
        });

        const templateContentMap = getTemplateContentMap({
          data: plan.plan.templateContents,
          filterFunc: content => content?.templateContent?.locale === locale
        });

        let normalizedPlan = null;

        if (subscriptionType(plan.plan.basePlan) === "CLINICAL") {
          normalizedPlan = normalizeClinicalPlan({
            plan: {
              ...plan,
              contentMap,
              addonTemplateContentMap,
              templateContentMap,
              bundleId
            },
            locale,
            isCompound
          });
        } else {
          normalizedPlan = normalizePlan({
            plan: {
              ...plan,
              contentMap,
              addonTemplateContentMap,
              templateContentMap,
              bundleId
            },
            locale
          });
        }
        plans.push(normalizedPlan);
      });

      setPlans(plans);
    }
  }, [plansData, isCompound]);

  const hasError = isError || plansIsError;

  return {
    isLoading:
      !hasError && (!data || !plansData || isLoading || arePlansLoading),
    bundle,
    plans,
    retry: mutate,
    isError: hasError,
    error
  };
};

/**
 * @typedef {object} Content
 * @property {string} contentKey
 * @property {string} contentValue
 */

/**
 *
 * @param {object} props
 * @param {Content[]} props.data
 * @param {function} [props.filterFunc]
 * @returns
 */
function getContentMap({ data, filterFunc = () => true }) {
  return new Map(
    data?.filter(filterFunc).map(content => {
      return [content.contentKey, content.contentValue];
    })
  );
}

/**
 * @typedef {object} TemplateContent
 * @property {string} templateContentKeyName
 * @property {object} templateContent
 * @property {string} templateContent.templateValue
 */

/**
 *
 * @param {object} props
 * @param {TemplateContent[]} props.data
 * @param {function} [props.filterFunc]
 * @returns
 */
export function getTemplateContentMap({ data, filterFunc = () => true }) {
  return new Map(
    data?.filter(filterFunc).map(content => {
      return [
        content.templateContentKeyName,
        content.templateContent.templateValue
      ];
    })
  );
}

/**
 * @typedef {object} Addon
 * @property {TemplateContent[]} templateContents
 */

/**
 *
 * @param {object} props
 * @param {Addon[]} props.data
 * @param {function} [props.filterFunc]
 * @returns
 */
function getAddonTemplateContentMap({ data, filterFunc = () => true }) {
  return data
    ? new Map(
        ...data.map(addon =>
          addon?.templateContents?.filter(filterFunc).map(content => {
            return [
              content.templateContentKeyName,
              content.templateContent.templateValue
            ];
          })
        )
      )
    : new Map();
}
